import { useAppearanceStore } from '@/store/appearance';
import { useAccountInfo } from '@/hooks/query/account';
import { ViewEnum } from '@/types';
import { useEffect, useRef, useState } from 'react';
import { useExternalScript } from '@nf/utils-common/hooks';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

export enum AnalyseKey {
	Base = 'nextanalyse',
	Refined = 'refined_nextanalyse',
	DataRefined = 'data-refined-nextanalyse',
}

const analyseKeys = [AnalyseKey.Base, AnalyseKey.Refined, AnalyseKey.DataRefined];

export const useIsNextAnalyseEnabled = () => {
	const viewFromStore = useAppearanceStore(state => state.view);
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const [isDisabled, setIsDisabled] = useState<boolean>(!user || user.ActStatus !== 2 || viewFromStore !== ViewEnum.mars || !SiteConfigsData?.EnableNextAnalyse || !SiteConfigsData?.BehaviorCatcherEntry);
	useEffect(() => {
		const currentlyIsDisabled = !user || user.ActStatus !== 2 || viewFromStore !== ViewEnum.mars || !SiteConfigsData?.EnableNextAnalyse || !SiteConfigsData?.BehaviorCatcherEntry;
		if (currentlyIsDisabled !== isDisabled) {
			setIsDisabled(currentlyIsDisabled);
		}
	}, [viewFromStore, user?.ActStatus, SiteConfigsData?.EnableNextAnalyse, SiteConfigsData?.BehaviorCatcherEntry]);
	return !isDisabled;
};

export const NextAnalyseProvider = () => {
	const { user } = useAccountInfo();
	const isNextAnalyseEnabled = useIsNextAnalyseEnabled();
	const { SiteConfigsData } = useGetSiteConfigs();
	useExternalScript(SiteConfigsData?.BehaviorCatcherEntry || '');
	
	useEffect(() => {
		const canNotActive = !isNextAnalyseEnabled || typeof localStorage.setItem !== 'function';
		if (canNotActive) {
			return;
		}
		analyseKeys.forEach(analyseKey => {
			const currentProfile = localStorage.getItem(analyseKey);
			try {
				const profile = !currentProfile ? {} : JSON.parse(currentProfile);
				if (profile['userId'] !== user?.ID) {
					profile['userId'] = user?.ID;
				}
				const siteId = parseInt(SiteConfigsData?.GetMessageID ?? '0');
				if (profile['siteId'] !== siteId) {
					profile['siteId'] = siteId;
				}
				localStorage.setItem(analyseKey, JSON.stringify(profile));
			} catch (exception) {
				console.error('Unexpected situation happened', exception);
			}
		});
	}, [isNextAnalyseEnabled, user?.ID, SiteConfigsData?.GetMessageID]);

	return null;
};

export const NextAnalyseCleaner = () => {
	useEffect(() => {
		try {
			analyseKeys.forEach(analyseKey => {
				const currentProfile = localStorage.getItem(analyseKey);
				if (currentProfile !== null) {
					localStorage.removeItem(analyseKey);
				}
			});
		} catch (exception) {
			console.error('Something wrong when cleaning existing next-analyse profile', exception);
		}
	}, []);
	return null;
};

export const useAnalyseAttribute = (key: string) => {
	const isNextAnalyseEnabled = useIsNextAnalyseEnabled();
	const viewFromStore = useAppearanceStore(state => state.view);
	const taggedElement = useRef<any>(null);

	useEffect(() => {
		const canNotActive = !isNextAnalyseEnabled || !key || !taggedElement.current || typeof taggedElement.current.setAttribute !== 'function';
		if (canNotActive) {
			return;
		}
		const hasNextAnalyze = taggedElement.current.hasAttribute(AnalyseKey.Base);
		if (viewFromStore === ViewEnum.mars && !hasNextAnalyze) {
			taggedElement.current.setAttribute(AnalyseKey.Base, key);
		} else if (viewFromStore !== ViewEnum.mars && hasNextAnalyze) {
			taggedElement.current.removeAttribute(AnalyseKey.Base);
		}
	}, [isNextAnalyseEnabled, key, viewFromStore]);
	
	return taggedElement;
};

export const useAnalyseAttributeWithToggle = (isActived: boolean, keys: string[]) => {
	const isNextAnalyseEnabled = useIsNextAnalyseEnabled();
	const viewFromStore = useAppearanceStore(state => state.view);
	const taggedElement = useRef<any>(null);
	useEffect(() => {
		const canNotActive = !isNextAnalyseEnabled || !Array.isArray(keys) || !taggedElement.current || typeof taggedElement.current.setAttribute !== 'function';
		if (canNotActive) {
			return;
		}
		const hasNextAnalyze = taggedElement.current.hasAttribute(AnalyseKey.Base);
		if (viewFromStore === ViewEnum.mars) {
			const valueShouldBe = keys[isActived ? 0 : 1];
			if (!hasNextAnalyze || taggedElement.current.getAttribute(AnalyseKey.Base) !== valueShouldBe) {
				taggedElement.current.setAttribute(AnalyseKey.Base, valueShouldBe);
			}
		} else if (hasNextAnalyze) {
			taggedElement.current.removeAttribute(AnalyseKey.Base);
		}
	}, [isActived, isNextAnalyseEnabled, keys, viewFromStore]);
	
	return taggedElement;
};

export type AnalyseConfig = {
	key: string | string[];
	analyseKey?: AnalyseKey;
};

export const useConfigsAnalyseAttribute = (configs: AnalyseConfig[]) => {
	const isNextAnalyseEnabled = useIsNextAnalyseEnabled();
	const viewFromStore = useAppearanceStore(state => state.view);
	const taggedElement = useRef<any>(null);
	
	useEffect(() => {
		const canNotActive = !isNextAnalyseEnabled;
		const currentElement = taggedElement.current;
		if (canNotActive || !currentElement || typeof currentElement.setAttribute !== 'function') {
			return;
		}

		configs.forEach(config => {
			const element = currentElement;
			
			const analyseKey = config.analyseKey || AnalyseKey.Base;
			const hasNextAnalyze = element.hasAttribute(analyseKey);
			if (viewFromStore === ViewEnum.mars && !hasNextAnalyze) {
				const attributeValue = Array.isArray(config.key) ? config.key.join(' ') : config.key;
				element.setAttribute(analyseKey, attributeValue);
			} else if (viewFromStore !== ViewEnum.mars && hasNextAnalyze) {
				element.removeAttribute(analyseKey);
			}
		});
	}, [configs, isNextAnalyseEnabled, viewFromStore]);

	return taggedElement;
};

type ToggleConfig = {
	key: string;
	analyseKey?: AnalyseKey;
	activeKey: string;
	inactiveKey: string;
	isActive: boolean;
};

export const useConfigsAnalyseAttributeWithToggle = (configs: ToggleConfig[]) => {
	const isNextAnalyseEnabled = useIsNextAnalyseEnabled();
	const viewFromStore = useAppearanceStore(state => state.view);
	const taggedElements = useRef<{ [key: string]: any }>({});
	
	useEffect(() => {
		const canNotActive = !isNextAnalyseEnabled;
		if (canNotActive) {
			return;
		}

		configs.forEach(config => {
			const element = taggedElements.current[config.key]?.current;
			if (!element || typeof element.setAttribute !== 'function') {
				return;
			}

			const analyseKey = config.analyseKey || AnalyseKey.Base;
			const hasNextAnalyze = element.hasAttribute(analyseKey);
			if (viewFromStore === ViewEnum.mars) {
				const valueShouldBe = config.isActive ? config.activeKey : config.inactiveKey;
				if (!hasNextAnalyze || element.getAttribute(analyseKey) !== valueShouldBe) {
					element.setAttribute(analyseKey, valueShouldBe);
				}
			} else if (hasNextAnalyze) {
				element.removeAttribute(analyseKey);
			}
		});
	}, [configs, isNextAnalyseEnabled, viewFromStore]);

	const getRef = (key: string) => {
		if (!taggedElements.current[key]) {
			taggedElements.current[key] = { current: null };
		}
		return taggedElements.current[key];
	};

	return { getRef };
};